// src/oidc/auth.js — Token exchange service for OIDC and legacy OAuth flows
// Uses native fetch (no axios dependency).  All helpers return null on error
// so callers can branch on the result without try/catch.

import {
  SIMBA_SERVICE_BASE_URL,
  OIDC_TOKEN_URL,
  OAUTH_TOKEN_URL,
  OAUTH_REVOKE_URL
} from '../config'

/**
 * Exchange an authorization code (or refresh token) for tokens via the OIDC
 * backend-for-frontend (BFF) token endpoint.
 *
 * @param {URLSearchParams} params — body parameters (grant_type, code, code_verifier, …)
 * @returns {Promise<Object|null>} parsed JSON response with access_token, or null on failure
 */
export async function oidcTokenExchange(params) {
  try {
    const res = await fetch(SIMBA_SERVICE_BASE_URL + OIDC_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      },
      body: params.toString()
    })
    if (!res.ok) {
      console.error('OIDC token exchange failed:', res.status)
      return null
    }
    const data = await res.json()
    if (!data.access_token) {
      console.error('OIDC token exchange response missing access_token:', data)
      return null
    }
    return data
  } catch (e) {
    console.error('OIDC token exchange error:', e)
    return null
  }
}

/**
 * Exchange an authorization code for tokens via the legacy OAuth token endpoint.
 *
 * @param {URLSearchParams} params — body parameters (grant_type, code, client_id, …)
 * @returns {Promise<Object|null>} parsed JSON response with access_token, or null on failure
 */
export async function legacyTokenExchange(params) {
  try {
    const res = await fetch(SIMBA_SERVICE_BASE_URL + OAUTH_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      },
      body: params.toString()
    })
    if (!res.ok) return null
    const text = await res.text()
    let data
    try {
      data = JSON.parse(text)
    } catch (e) {
      return null
    }
    if (!data.access_token) {
      console.error('Token exchange response missing access_token:', data)
      return null
    }
    return data
  } catch (e) {
    console.error('Token exchange error:', e)
    return null
  }
}

/**
 * Revoke an access or refresh token via the legacy OAuth revocation endpoint.
 * Failures are silently caught — revocation is best-effort.
 *
 * @param {string} token — the token to revoke
 * @returns {Promise<void>}
 */
export async function revokeToken(token) {
  const params = new URLSearchParams()
  params.append('token', token)
  try {
    await fetch(SIMBA_SERVICE_BASE_URL + OAUTH_REVOKE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      },
      body: params.toString()
    })
  } catch (e) {
    console.warn('Token revocation failed:', e)
  }
}



// WEBPACK FOOTER //
// ./src/oidc/auth.js